import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { collection, getDocs, addDoc, updateDoc, deleteDoc, deleteField, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, IconButton, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Typography, Box, Chip, Alert
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";

function KullaniciYonetimi({ sube }) {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const subeFromUrl = queryParams.get("sube") || "Bilinmeyen Şube";
  const [kullanicilar, setKullanicilar] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [newKullanici, setNewKullanici] = useState({ ad: "", email: "", rol: "user" });
  const [editKullanici, setEditKullanici] = useState(null);

  useEffect(() => {
    const fetchKullanicilar = async () => {
      setLoading(true);
      try {
        const kullanicilarCollection = collection(db, "kullanicilar");
        const kullanicilarSnapshot = await getDocs(kullanicilarCollection);
        const kullaniciList = kullanicilarSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setKullanicilar(kullaniciList);
        setLoading(false);
      } catch (err) {
        console.error("Kullanıcıları alma hatası:", err);
        setError(`Hata: ${err.message}`);
        setLoading(false);
      }
    };
    fetchKullanicilar();
  }, []);

  const handleAddKullanici = async () => {
    try {
      const kullanicilarCollection = collection(db, "kullanicilar");
      await addDoc(kullanicilarCollection, { ...newKullanici });
      setNewKullanici({ ad: "", email: "", rol: "user" });
      setOpenAddDialog(false);
      const kullanicilarSnapshot = await getDocs(kullanicilarCollection);
      setKullanicilar(kullanicilarSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setError("");
    } catch (err) {
      console.error("Kullanıcı ekleme hatası:", err);
      setError(`Hata: ${err.message}`);
    }
  };

  const handleEditKullanici = async () => {
    try {
      const kullaniciDoc = doc(db, "kullanicilar", editKullanici.id);
      const { sifre, ...editWithoutPassword } = editKullanici;
      // Şifre bu koleksiyonda hiçbir zaman tutulmamalı (Firebase Auth'un kendi
      // şifre yönetimi var) — eski kayıtlarda kalmış olabilecek alanı da temizle.
      await updateDoc(kullaniciDoc, { ...editWithoutPassword, sifre: deleteField() });
      setOpenEditDialog(false);
      const kullanicilarCollection = collection(db, "kullanicilar");
      const kullanicilarSnapshot = await getDocs(kullanicilarCollection);
      setKullanicilar(kullanicilarSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setError("");
    } catch (err) {
      console.error("Kullanıcı güncelleme hatası:", err);
      setError(`Hata: ${err.message}`);
    }
  };

  const handleDeleteKullanici = async (id) => {
    if (window.confirm("Bu kullanıcıyı silmek istediğinize emin misiniz?")) {
      try {
        const kullaniciDoc = doc(db, "kullanicilar", id);
        await deleteDoc(kullaniciDoc);
        setKullanicilar(kullanicilar.filter(k => k.id !== id));
        setError("");
      } catch (err) {
        console.error("Kullanıcı silme hatası:", err);
        setError(`Hata: ${err.message}`);
      }
    }
  };

  const handleOpenEditDialog = async (kullanici) => {
    try {
      const kullaniciDocRef = doc(db, "kullanicilar", kullanici.id);
      const kullaniciSnap = await getDoc(kullaniciDocRef);

      if (kullaniciSnap.exists()) {
        const fullData = kullaniciSnap.data();
        setEditKullanici({ ...kullanici, ...fullData });
      } else {
        setEditKullanici({ ...kullanici });
      }
    } catch (err) {
      console.error("Kullanıcı detay alma hatası:", err);
      setEditKullanici({ ...kullanici });
    }

    setOpenEditDialog(true);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="textSecondary">Yükleniyor...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  return (
    <Box
      className="ky-page"
      sx={{
        p: 2,
        background: "linear-gradient(165deg, #f5f2eb 0%, #ece6da 45%, #e2dacd 100%)",
        minHeight: "auto",
        fontFamily: "'Inter', 'Segoe UI', sans-serif"
      }}
    >
      <Paper
        className="ky-paper"
        elevation={0}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid rgba(212, 175, 98, 0.28)",
          boxShadow: "0 14px 30px rgba(15, 23, 42, 0.16), inset 0 1px 0 rgba(255,255,255,0.7)"
        }}
      >
        <Box
          className="ky-header"
          sx={{
            background: "linear-gradient(130deg, #121820 0%, #0c1118 52%, #070b10 100%)",
            color: "#f4f1eb",
            p: 2.5,
            textAlign: "center",
            borderBottom: "1px solid rgba(212, 175, 98, 0.22)"
          }}
        >
          <Typography variant="h5" fontWeight={600} sx={{ letterSpacing: "0.02em", fontFamily: "Georgia, serif", fontSize: "2rem" }}>
            Kullanıcı Yönetimi
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5, fontSize: "0.85rem" }}>
            Şube: {subeFromUrl}
          </Typography>
        </Box>

        <Box className="ky-content" sx={{ p: 2.5, background: "linear-gradient(180deg, rgba(252,251,248,0.98), rgba(245,241,235,0.96))" }}>
          <Box className="ky-toolbar" sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
            <Button
              className="ky-add-btn"
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={{
                background: "linear-gradient(180deg, #1f2937 0%, #0f172a 100%)",
                border: "1px solid rgba(212, 175, 98, 0.4)",
                fontWeight: 700,
                textTransform: "none",
                px: 2.5,
                py: 0.9,
                fontSize: "0.88rem",
                borderRadius: "10px",
                boxShadow: "0 8px 18px rgba(15, 23, 42, 0.28)",
                "&:hover": {
                  background: "linear-gradient(180deg, #273549 0%, #152235 100%)"
                }
              }}
            >
              Yeni Kullanıcı Ekle
            </Button>
          </Box>

          <TableContainer
            className="ky-table-wrap"
            component={Paper}
            elevation={0}
            sx={{
              borderRadius: 2.5,
              border: "1px solid rgba(212, 175, 98, 0.22)",
              boxShadow: "0 8px 20px rgba(15,23,42,0.07)"
            }}
          >
            <Table className="ky-table" size="small">
              <TableHead>
                <TableRow sx={{ background: "linear-gradient(180deg, #111827 0%, #0b1220 100%)" }}>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, fontSize: "0.86rem", letterSpacing: "0.03em", py: 1.1 }}>Ad</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, fontSize: "0.86rem", letterSpacing: "0.03em", py: 1.1 }}>E-posta</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, fontSize: "0.86rem", letterSpacing: "0.03em", py: 1.1 }} align="center">Rol</TableCell>
                  <TableCell sx={{ color: "#f8fafc", fontWeight: 800, fontSize: "0.86rem", letterSpacing: "0.03em", py: 1.1 }} align="center">İşlemler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {kullanicilar.map((kullanici) => (
                  <TableRow
                    key={kullanici.id}
                    hover
                    sx={{
                      "&:nth-of-type(odd)": { backgroundColor: "rgba(248, 250, 252, 0.9)" },
                      "&:nth-of-type(even)": { backgroundColor: "rgba(241, 245, 249, 0.75)" },
                      "&:hover": { backgroundColor: "rgba(212,175,98,0.12)" }
                    }}
                  >
                    <TableCell className="ky-cell-name" data-label="Ad" sx={{ fontWeight: 700, color: "#111827", fontSize: "0.86rem", py: 0.85 }}>{kullanici.ad}</TableCell>
                    <TableCell className="ky-cell-email" data-label="E-posta" sx={{ color: "#334155", fontWeight: 500, fontSize: "0.82rem", py: 0.85 }}>{kullanici.email}</TableCell>
                    <TableCell className="ky-cell-role" data-label="Rol" align="center">
                      <Chip
                        label={kullanici.rol === "admin" ? "Yönetici" : "Kullanıcı"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.7rem",
                          height: "22px",
                          color: kullanici.rol === "admin" ? "#7f1d1d" : "#1e3a8a",
                          backgroundColor: kullanici.rol === "admin" ? "rgba(254,226,226,0.9)" : "rgba(219,234,254,0.9)",
                          border: `1px solid ${kullanici.rol === "admin" ? "rgba(239,68,68,0.35)" : "rgba(59,130,246,0.35)"}`
                        }}
                      />
                    </TableCell>
                    <TableCell className="ky-cell-actions" data-label="İşlemler" align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenEditDialog(kullanici)}
                        sx={{
                          color: "#1e293b",
                          mr: 1,
                          border: "1px solid rgba(148,163,184,0.35)",
                          background: "rgba(255,255,255,0.7)",
                          "&:hover": { background: "rgba(212,175,98,0.18)" }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteKullanici(kullanici.id)}
                        sx={{
                          color: "#b91c1c",
                          border: "1px solid rgba(239,68,68,0.3)",
                          background: "rgba(255,255,255,0.7)",
                          "&:hover": { background: "rgba(254,226,226,0.8)" }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Paper>

      {/* Yeni Kullanıcı Ekle Dialog */}
      <Dialog
        className="ky-dialog"
        open={openAddDialog}
        onClose={() => setOpenAddDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ background: "linear-gradient(180deg, #111827 0%, #0b1220 100%)", color: "#f8fafc", fontWeight: 700 }}>
          Yeni Kullanıcı Ekle
        </DialogTitle>
        <DialogContent sx={{ pt: 3, background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)" }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Bu ekran yalnızca bilgi amaçlı bir kayıt oluşturur; gerçek giriş yapabilen bir hesap
            değildir. Gerçek giriş bilgisi (Firebase Authentication) yönetici tarafından ayrıca
            oluşturulmalıdır.
          </Alert>
          <TextField label="Ad" fullWidth margin="normal" value={newKullanici.ad} onChange={(e) => setNewKullanici({ ...newKullanici, ad: e.target.value })} />
          <TextField label="E-posta" fullWidth margin="normal" value={newKullanici.email} onChange={(e) => setNewKullanici({ ...newKullanici, email: e.target.value })} />
          <Select
            fullWidth
            value={newKullanici.rol}
            onChange={(e) => setNewKullanici({ ...newKullanici, rol: e.target.value })}
            displayEmpty
            sx={{ mt: 2 }}
          >
            <MenuItem value="user">Kullanıcı</MenuItem>
            <MenuItem value="admin">Yönetici</MenuItem>
          </Select>
        </DialogContent>
        <DialogActions sx={{ p: 3, background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)" }}>
          <Button onClick={() => setOpenAddDialog(false)} sx={{ color: "#b91c1c", fontWeight: 700 }}>İptal</Button>
          <Button onClick={handleAddKullanici} variant="contained" sx={{ background: "linear-gradient(180deg, #1f2937 0%, #0f172a 100%)", border: "1px solid rgba(212,175,98,0.35)", fontWeight: 700 }}>Ekle</Button>
        </DialogActions>
      </Dialog>

      {/* Kullanıcı Düzenle Dialog */}
      {editKullanici && (
        <Dialog
          className="ky-dialog"
          open={openEditDialog}
          onClose={() => setOpenEditDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ background: "linear-gradient(180deg, #111827 0%, #0b1220 100%)", color: "#f8fafc", fontWeight: 700 }}>
            Kullanıcı Düzenle
          </DialogTitle>
          <DialogContent sx={{ pt: 3, background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)" }}>
            <TextField label="Ad" fullWidth margin="normal" value={editKullanici.ad} onChange={(e) => setEditKullanici({ ...editKullanici, ad: e.target.value })} />
            <TextField label="E-posta" fullWidth margin="normal" value={editKullanici.email} onChange={(e) => setEditKullanici({ ...editKullanici, email: e.target.value })} />
            <Select
              fullWidth
              value={editKullanici.rol}
              onChange={(e) => setEditKullanici({ ...editKullanici, rol: e.target.value })}
              sx={{ mt: 2 }}
            >
              <MenuItem value="user">Kullanıcı</MenuItem>
              <MenuItem value="admin">Yönetici</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions sx={{ p: 3, background: "linear-gradient(180deg, #fcfbf8 0%, #f5f1eb 100%)" }}>
            <Button onClick={() => setOpenEditDialog(false)} sx={{ color: "#b91c1c", fontWeight: 700 }}>İptal</Button>
            <Button onClick={handleEditKullanici} variant="contained" sx={{ background: "linear-gradient(180deg, #1f2937 0%, #0f172a 100%)", border: "1px solid rgba(212,175,98,0.35)", fontWeight: 700 }}>Kaydet</Button>
          </DialogActions>
        </Dialog>
      )}
      <style>{`
        /* --- MOBİL: Kullanıcı Yönetimi (masaüstü sx stillerine dokunulmaz) --- */
        @media (max-width: 1649px), (max-width: 1400px) and (any-pointer: coarse) {
          .ky-page {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            min-height: 0 !important;
            height: auto !important;
            overflow-x: hidden !important;
            padding: 6px 2px 80px !important;
            box-sizing: border-box !important;
          }

          .ky-paper {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            border-radius: 12px !important;
          }

          .ky-header {
            padding: 16px 12px !important;
          }

          .ky-header .MuiTypography-h5 {
            font-size: 1.35rem !important;
            line-height: 1.2 !important;
          }

          .ky-content {
            padding: 12px 8px !important;
          }

          .ky-toolbar {
            justify-content: stretch !important;
            margin-bottom: 12px !important;
          }

          .ky-add-btn {
            width: 100% !important;
            min-height: 44px !important;
          }

          .ky-table-wrap {
            overflow: visible !important;
            border: none !important;
            box-shadow: none !important;
            background: transparent !important;
          }

          .ky-table {
            min-width: 0 !important;
          }

          .ky-table thead {
            display: none !important;
          }

          .ky-table tbody tr {
            display: block !important;
            margin-bottom: 10px !important;
            border: 1px solid rgba(212, 175, 98, 0.22) !important;
            border-radius: 12px !important;
            overflow: hidden !important;
            background: rgba(255, 255, 255, 0.95) !important;
            box-shadow: 0 4px 12px rgba(15, 23, 42, 0.06) !important;
          }

          .ky-table tbody tr:nth-of-type(odd),
          .ky-table tbody tr:nth-of-type(even) {
            background-color: rgba(255, 255, 255, 0.95) !important;
          }

          .ky-table tbody td {
            display: flex !important;
            justify-content: space-between !important;
            align-items: center !important;
            gap: 10px !important;
            padding: 10px 12px !important;
            border: none !important;
            border-bottom: 1px solid rgba(15, 23, 42, 0.06) !important;
            text-align: right !important;
            font-size: 13px !important;
            word-break: break-word !important;
          }

          .ky-table tbody td:last-child {
            border-bottom: none !important;
            justify-content: flex-end !important;
          }

          .ky-table tbody td::before {
            content: attr(data-label) !important;
            font-weight: 700 !important;
            color: #64748b !important;
            font-size: 12px !important;
            text-align: left !important;
            flex-shrink: 0 !important;
          }

          .ky-cell-actions::before {
            margin-right: auto !important;
          }

          .ky-dialog .MuiDialog-paper {
            width: calc(100vw - 32px) !important;
            max-width: calc(100vw - 32px) !important;
            margin: 16px !important;
          }

          .ky-dialog .MuiInputBase-input {
            font-size: 16px !important;
          }

          .ky-dialog .MuiDialogActions-root {
            flex-wrap: wrap !important;
            gap: 8px !important;
          }

          .ky-dialog .MuiDialogActions-root .MuiButton-root {
            flex: 1 1 auto !important;
            min-height: 40px !important;
          }
        }
      `}</style>
    </Box>
  );
}

export default KullaniciYonetimi;